import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { fetchPublicPortfolio } from '../api'
import type { PublicPortfolioResponse, Internship, InternshipMilestone } from '../types'
import NotFoundPage from './NotFoundPage'

export default function PortfolioPage() {
  const { uniqueId } = useParams<{ uniqueId: string }>()
  const [data, setData] = useState<PublicPortfolioResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedExperiences, setExpandedExperiences] = useState<Set<string>>(new Set())
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!uniqueId) {
      setError('No portfolio ID provided')
      setLoading(false)
      return
    }

    const loadPortfolio = async () => {
      try {
        setLoading(true)
        const result = await fetchPublicPortfolio(uniqueId)
        setData(result)
        // Auto-expand first experience if there's only one
        if (result.internships?.length === 1) {
          setExpandedExperiences(new Set([result.internships[0].id]))
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load portfolio')
      } finally {
        setLoading(false)
      }
    }

    loadPortfolio()
  }, [uniqueId])

  const toggleExperience = (id: string) => {
    setExpandedExperiences(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleTopic = (id: string) => {
    setExpandedTopics(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return <NotFoundPage message={error || undefined} />
  }

  const { portfolio, internships } = data
  const displayName = portfolio.display_name || portfolio.unique_id

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{displayName}</h1>
              {portfolio.bio && (
                <p className="text-gray-600 max-w-2xl">{portfolio.bio}</p>
              )}
              <div className="flex items-center gap-4 mt-4">
                {portfolio.linkedin_url && (
                  <a 
                    href={portfolio.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <i className="fa-brands fa-linkedin text-lg"></i>
                    <span className="text-sm">LinkedIn</span>
                  </a>
                )}
                {portfolio.github_url && (
                  <a 
                    href={portfolio.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <i className="fa-brands fa-github text-lg"></i>
                    <span className="text-sm">GitHub</span>
                  </a>
                )}
                {portfolio.website_url && (
                  <a 
                    href={portfolio.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <i className="fa-solid fa-globe text-lg"></i>
                    <span className="text-sm">Website</span>
                  </a>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              <a 
                href="https://learnplace.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <img 
                  src="https://learnplace.ai/logo.svg" 
                  alt="Learn Place" 
                  className="h-8 opacity-50 hover:opacity-100 transition-opacity"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon="fa-briefcase" 
            value={internships.length} 
            label="Verified Experience" 
          />
          <StatCard 
            icon="fa-check-circle" 
            value={internships.filter(i => i.status === 'completed').length} 
            label="Completed" 
          />
          <StatCard 
            icon="fa-tasks" 
            value={internships.reduce((acc, i) => acc + i.milestones.filter(m => m.status === 'completed').length, 0)} 
            label="Topics" 
          />
          <StatCard 
            icon="fa-clock" 
            value={`${internships.reduce((acc, i) => acc + (i.goal?.estimated_hours || 0), 0)}h`} 
            label="Total Credits" 
          />
        </div>

        {/* Confirmed Skills Section */}
        {(() => {
          // Collect all skills from completed milestones
          const confirmedSkills = new Set<string>()
          internships.forEach(internship => {
            internship.milestones
              .filter(m => m.status === 'completed')
              .forEach(milestone => {
                const brief = internship.briefs?.find(b => b.milestone_id === milestone.id)
                if (brief?.skills_required) {
                  brief.skills_required.forEach(skill => confirmedSkills.add(skill))
                }
              })
          })
          const skillsArray = Array.from(confirmedSkills)
          
          if (skillsArray.length === 0) return null
          
          return (
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirmed Skills</h2>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex flex-wrap gap-2">
                  {skillsArray.map((skill, i) => (
                    <span 
                      key={i} 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 text-sm font-medium rounded-full"
                    >
                      <i className="fa-solid fa-check-circle text-green-600 text-xs"></i>
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </section>
          )
        })()}

        {/* Verified Experience Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Verified Experience</h2>
          
          {internships.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <i className="fa-solid fa-briefcase text-4xl text-gray-300 mb-4"></i>
              <p className="text-gray-600">No verified experience to display yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {internships.map(internship => (
                <ExperienceCard 
                  key={internship.id}
                  experience={internship}
                  isExpanded={expandedExperiences.has(internship.id)}
                  onToggle={() => toggleExperience(internship.id)}
                  expandedTopics={expandedTopics}
                  onToggleTopic={toggleTopic}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer - Pinned to bottom */}
      <footer className="fixed bottom-0 left-0 right-0 border-t border-gray-200 py-4 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            Portfolio powered by{' '}
            <a 
              href="https://learnplace.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 font-medium"
            >
              Learn Place
            </a>
          </p>
        </div>
      </footer>
      {/* Spacer for fixed footer */}
      <div className="h-16"></div>
    </div>
  )
}

// Stat Card Component
function StatCard({ icon, value, label }: { icon: string; value: string | number; label: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
          <i className={`fa-solid ${icon} text-gray-600`}></i>
        </div>
        <div>
          <div className="text-xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{label}</div>
        </div>
      </div>
    </div>
  )
}

// Experience Card Component (Subject with Topics)
function ExperienceCard({ 
  experience, 
  isExpanded, 
  onToggle,
  expandedTopics,
  onToggleTopic
}: { 
  experience: Internship
  isExpanded: boolean
  onToggle: () => void
  expandedTopics: Set<string>
  onToggleTopic: (id: string) => void
}) {
  const completedTopics = experience.milestones.filter(m => m.status === 'completed').length
  const totalTopics = experience.milestones.length
  const totalCredits = experience.milestones.reduce((acc, m) => acc + (m.estimated_hours || 0), 0)
  const completedCredits = experience.milestones
    .filter(m => m.status === 'completed')
    .reduce((acc, m) => acc + (m.estimated_hours || 0), 0)

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header - Always visible */}
      <button 
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center">
            <i className="fa-solid fa-briefcase text-white text-lg"></i>
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">{experience.job_title}</h3>
            <p className="text-sm text-gray-500">{experience.industry} • {totalCredits} credits</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="flex items-center gap-2">
              {experience.status === 'completed' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                  <i className="fa-solid fa-check-circle"></i>
                  Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                  <i className="fa-solid fa-spinner"></i>
                  In Progress
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {completedTopics} / {totalTopics} topics • {completedCredits}h earned
            </p>
          </div>
          <i className={`fa-solid fa-chevron-down text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
        </div>
      </button>

      {/* Progress Bar */}
      <div className="px-6 pb-4">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-500 rounded-full transition-all duration-500"
            style={{ width: `${experience.progress_percentage}%` }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Goal Section */}
          {experience.goal && (
            <div className="px-6 py-4 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Goal</h4>
              <p className="text-gray-900 font-medium">{experience.goal.title}</p>
              <p className="text-gray-600 text-sm mt-1">{experience.goal.description}</p>
            </div>
          )}

          {/* Topics */}
          <div className="px-6 py-4">
            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Topics</h4>
            <div className="space-y-3">
              {experience.milestones
                .sort((a, b) => a.order - b.order)
                .map(milestone => (
                  <TopicItem 
                    key={milestone.id}
                    topic={milestone}
                    experience={experience}
                    isExpanded={expandedTopics.has(milestone.id)}
                    onToggle={() => onToggleTopic(milestone.id)}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Topic Item Component (with submission, feedback, and video assessment)
function TopicItem({ 
  topic, 
  experience,
  isExpanded,
  onToggle
}: { 
  topic: InternshipMilestone
  experience: Internship
  isExpanded: boolean
  onToggle: () => void
}) {
  const submission = experience.submissions?.find(s => s.milestone_id === topic.id)
  const finalReview = experience.final_reviews?.find(r => r.milestone_id === topic.id)
  const brief = experience.briefs?.find(b => b.milestone_id === topic.id)
  const hasDetails = brief || submission || finalReview

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={hasDetails ? onToggle : undefined}
        className={`w-full px-4 py-3 flex items-center gap-3 ${hasDetails ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          topic.status === 'completed' 
            ? 'bg-green-100 text-green-600' 
            : topic.status === 'in_progress'
            ? 'bg-blue-100 text-blue-600'
            : 'bg-gray-100 text-gray-400'
        }`}>
          {topic.status === 'completed' ? (
            <i className="fa-solid fa-check"></i>
          ) : (
            <span className="text-sm font-medium">{topic.order}</span>
          )}
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900">{topic.title}</p>
          <p className="text-sm text-gray-500">{topic.estimated_hours} credits</p>
        </div>
        {hasDetails && (
          <i className={`fa-solid fa-chevron-down text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
        )}
      </button>

      {/* Expanded Details - Shows Submission, Feedback, and Video Assessment together */}
      {isExpanded && hasDetails && (
        <div className="px-4 py-4 border-t border-gray-100 bg-gray-50">
          {/* Brief Section */}
          {brief && (
            <div className="mb-6">
              <h5 className="text-sm font-medium text-gray-700 mb-3">
                <i className="fa-solid fa-clipboard-list mr-2"></i>
                Topic Brief
              </h5>
              
              {/* Full Description */}
              {brief.full_description && (
                <div className="mb-4">
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{brief.full_description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Expectations */}
                {brief.expectations && brief.expectations.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-bullseye"></i>
                      Expectations
                    </p>
                    <ul className="text-sm text-blue-700 space-y-1">
                      {brief.expectations.map((e, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>{e}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Acceptance Criteria */}
                {brief.acceptance_criteria && brief.acceptance_criteria.length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-check-double"></i>
                      Acceptance Criteria
                    </p>
                    <ul className="text-sm text-purple-700 space-y-1">
                      {brief.acceptance_criteria.map((c, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-purple-500 mt-1">•</span>
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Learning Objectives */}
                {brief.learning_objectives && brief.learning_objectives.length > 0 && (
                  <div className="bg-indigo-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-indigo-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-graduation-cap"></i>
                      Learning Objectives
                    </p>
                    <ul className="text-sm text-indigo-700 space-y-1">
                      {brief.learning_objectives.map((o, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-indigo-500 mt-1">•</span>
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Skills Required */}
                {brief.skills_required && brief.skills_required.length > 0 && (
                  <div className="bg-teal-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-teal-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-tools"></i>
                      Skills Required
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {brief.skills_required.map((s, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submission & Feedback Section */}
          {submission && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                <i className="fa-solid fa-file-alt mr-2"></i>
                Submission & Feedback
              </h5>
              
              {/* Submission Content */}
              {submission.submission_content && (
                <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Submitted Work</p>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{submission.submission_content}</p>
                </div>
              )}
              
              {submission.feedback_summary && (
                <div className="bg-gray-100 rounded-lg p-3 mb-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Feedback Summary</p>
                  <p className="text-gray-700 text-sm">{submission.feedback_summary}</p>
                </div>
              )}
              
              {submission.feedback && submission.feedback !== submission.feedback_summary && (
                <div className="bg-gray-100 rounded-lg p-3 mb-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Detailed Feedback</p>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{submission.feedback}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {submission.strengths && submission.strengths.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-check-circle"></i>
                      Strengths
                    </p>
                    <ul className="text-sm text-green-700 space-y-1">
                      {submission.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-500 mt-1">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {submission.improvements && submission.improvements.length > 0 && (
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-lightbulb"></i>
                      Areas for Improvement
                    </p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      {submission.improvements.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Video Assessment Section */}
          {finalReview ? (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <i className="fa-solid fa-video mr-2"></i>
                Video Assessment
                {finalReview.passed && (
                  <span className="ml-2 inline-flex items-center gap-1 text-green-600 text-xs bg-green-100 px-2 py-0.5 rounded-full">
                    <i className="fa-solid fa-check-circle"></i>
                    Passed
                  </span>
                )}
              </h5>
              {finalReview.video_url && (
                <div className="mb-3">
                  <video 
                    src={finalReview.video_url}
                    controls
                    className="w-full max-w-md rounded-lg"
                    preload="metadata"
                  />
                </div>
              )}
              {finalReview.feedback && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Assessment Feedback</p>
                  <p className="text-gray-700 text-sm">{finalReview.feedback}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {finalReview.strengths && finalReview.strengths.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-check-circle"></i>
                      Strengths
                    </p>
                    <ul className="text-sm text-green-700 space-y-1">
                      {finalReview.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-500 mt-1">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {finalReview.improvements && finalReview.improvements.length > 0 && (
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                      <i className="fa-solid fa-lightbulb"></i>
                      Areas for Improvement
                    </p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      {finalReview.improvements.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ) : submission && (
            <div className="bg-gray-100 rounded-lg p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <i className="fa-solid fa-video text-gray-400"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Video Assessment Pending</p>
                <p className="text-xs text-gray-500">The video assessment for this topic is being processed</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
